/* eslint-disable @typescript-eslint/naming-convention */
import { FC, useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { Button, Flex, Text, TextField } from '@servicetitan/anvil2';
import { TXVSizeRequestDto } from '../../models/calculator.dto';
import { FormState } from 'formstate';
import { InputFieldState } from '@servicetitan/form-state';
import { useDependencies } from '@servicetitan/react-ioc';
import { CalculatorStore } from '../../stores/calculator.store';
import { observer } from 'mobx-react';

export const TxvSizeCalculatorForm: FC<{
    className?: string;
    onSubmit?: (params: TXVSizeRequestDto) => void;
}> = observer(({ className }) => {
    const [store] = useDependencies<[CalculatorStore]>(CalculatorStore);
    const [isLoading, setIsLoading] = useState(false);
    const formState = useMemo(() => {
        return new FormState({
            tonnage: new InputFieldState<string>('').validators(value => !value && 'Required'),
            minimum_capacity: new InputFieldState<string>('').validators(
                value => !value && 'Required'
            ),
        });
    }, []);

    const handleSubmit = useCallback(async () => {
        const validateResult = await formState.validate();
        if (validateResult.hasError) {
            return;
        }
        const params: TXVSizeRequestDto = {
            tool_name: 'txv-size',
            tool_input: {
                tonnage: Number(formState.$.tonnage.$),
                minimum_capacity: Number(formState.$.minimum_capacity.$),
            },
        };
        setIsLoading(true);
        await store.submitCalculation(params);
        setIsLoading(false);
    }, [formState, store]);

    return (
        <Flex direction="column" gap="4" className={classNames('w-100-i', className)}>
            <Text size="small" subdued>
                Please provide the following info:
            </Text>
            <TextField
                required
                error={formState.$.tonnage.error}
                label="Nominal Tonnage"
                type="number"
                step={0.5}
                hint="Per circuit"
                value={formState.$.tonnage.value}
                onChange={e => formState.$.tonnage.onChange(e.target.value)}
            />
            <TextField
                required
                error={formState.$.minimum_capacity.error}
                label="Minimum Capacity"
                hint="Smallest compressor stage per circuit"
                type="number"
                value={formState.$.minimum_capacity.value}
                onChange={e => formState.$.minimum_capacity.onChange(e.target.value)}
            />

            <Button
                loading={isLoading}
                alignSelf="self-end"
                appearance="primary"
                size="small"
                onClick={handleSubmit}
            >
                Submit
            </Button>
        </Flex>
    );
});
